import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

interface WebkitWindow extends Window {
  webkitAudioContext?: typeof AudioContext;
}

export function AudioVisualizer({
  deviceId,
  enabled = true,
  level,
}: { isRecording: boolean; deviceId?: string; enabled?: boolean; level?: number }) {
  const [bars, setBars] = useState<number[]>(new Array(12).fill(4));
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const dataArrayRef = useRef<Uint8Array<ArrayBuffer> | null>(null);
  const animationFrameRef = useRef<number>(0);
  const streamRef = useRef<MediaStream | null>(null);

  const externalMeter = level !== undefined;
  useEffect(() => {
    if (!externalMeter) return;
    const strength = enabled ? Math.max(0, Math.min(1, level ?? 0)) : 0;
    setBars(Array.from({ length: 12 }, (_, i) => Math.max(4, strength * 70 * (1 - (Math.abs(i - 5.5) / 5.5) * 0.3))));
  }, [externalMeter, level, enabled]);

  useEffect(() => {
    if (externalMeter) return;
    let cancelled = false;
    if (!enabled) {
      setBars(new Array(12).fill(4));
      return;
    }
    // Initialize audio context and microphone access
    const initAudio = async () => {
      try {
        // Request microphone access with specific device if provided
        const constraints: MediaStreamConstraints = {
          audio: deviceId ? { deviceId: { exact: deviceId } } : true,
        };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        if (cancelled) {
          for (const track of stream.getTracks()) track.stop();
          return;
        }
        streamRef.current = stream;

        // Create audio context and analyzer
        const AudioContextClass = window.AudioContext || (window as unknown as WebkitWindow).webkitAudioContext;
        if (!AudioContextClass) {
          throw new Error('AudioContext not supported');
        }
        const audioContext = new AudioContextClass();
        audioContextRef.current = audioContext;

        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 64; // Lower fftSize = fewer frequency bins
        analyser.smoothingTimeConstant = 0.8; // Smooth out the data
        analyserRef.current = analyser;

        // Connect microphone to analyzer
        const source = audioContext.createMediaStreamSource(stream);
        source.connect(analyser);

        // Create data array for frequency data
        const bufferLength = analyser.frequencyBinCount;
        dataArrayRef.current = new Uint8Array(new ArrayBuffer(bufferLength));

        // Start animation loop
        updateBars();
      } catch (error) {
        console.error('Error accessing microphone:', error);
        // Fall back to flat bars if mic access fails
        setBars(new Array(12).fill(4));
      }
    };

    const updateBars = () => {
      if (!analyserRef.current || !dataArrayRef.current) return;

      // Get frequency data from microphone
      analyserRef.current.getByteFrequencyData(dataArrayRef.current);

      // Calculate average volume from lower frequencies (where most audio energy is)
      // Use only first 40% of frequency bins (lower frequencies)
      const maxIndex = Math.floor(dataArrayRef.current.length * 0.4);
      let sum = 0;
      for (let i = 0; i < maxIndex; i++) {
        sum += dataArrayRef.current[i];
      }
      const avgVolume = sum / maxIndex;

      // Map frequency data to 12 bars
      // Use lower frequency range and distribute across all bars with wave pattern
      const newBars = Array.from({ length: 12 }, (_, i) => {
        // Sample from lower frequency range (first 40% of bins)
        const index = Math.floor((i / 12) * maxIndex);
        const frequencyValue = dataArrayRef.current?.[index] ?? 0;

        // Combine frequency data with average volume for better distribution
        const combinedValue = frequencyValue * 0.6 + avgVolume * 0.4;

        // If very low, keep it flat at 4%
        if (combinedValue < 10) return 4;

        // Create wave pattern: center bars are taller
        const centerDistance = Math.abs(i - 5.5) / 5.5; // 0 at center, 1 at edges
        const centerBoost = 1 - centerDistance * 0.3; // 0.7 at edges, 1.0 at center

        // Add slight random variation for more natural look (±5%)
        const randomVariation = (Math.random() - 0.5) * 10;

        // Scale to 4-70% range with wave pattern
        const baseHeight = (combinedValue / 255) * 70 * centerBoost;

        return Math.min(70, Math.max(4, baseHeight + randomVariation));
      });

      setBars(newBars);

      // Continue animation loop
      animationFrameRef.current = requestAnimationFrame(updateBars);
    };

    // Initialize audio on mount
    initAudio();

    // Cleanup on unmount or device change
    return () => {
      cancelled = true;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      if (streamRef.current) {
        for (const track of streamRef.current.getTracks()) {
          track.stop();
        }
      }
    };
  }, [deviceId, enabled, externalMeter]);

  return (
    <div className="flex h-12 w-32 items-center justify-center gap-1" aria-label="Audio Visualizer">
      {bars.map((height, i) => (
        <motion.div
          // biome-ignore lint/suspicious/noArrayIndexKey: bars array is fixed size
          key={i}
          className="w-1 rounded-full bg-primary"
          animate={{ height: `${height}%` }}
          transition={{
            duration: 0.08,
            ease: 'easeOut',
          }}
        />
      ))}
    </div>
  );
}
